import React, { useId, useState } from 'react';
import {
  TEMPLATE_NAME_MAX, saveableGroups, defaultSaveSelection, saveGroupIds, everyTicked, templateNameError,
  templateErrorInfo,
} from '../../utils/lionAutoTemplates';
import { toggleSelection, sectionsText, rowsText, qtyText } from '../../utils/lionAutoChecker';

// Templates → "Save this show as a template" / "Update from this show": a
// name and a tick list of this show's groups. A group made by Lion Checker,
// or linked to another template, can't be ticked (its reason shows). A new
// template ticks every saveable group; an update ticks the groups already
// linked to it (every saveable one when this show has none). Ticks and the
// name are held as null until touched, so the defaults always follow the
// groups on screen. A write whose outcome is unknown (no answer, a 502 /
// 504) reads in warn style: reload to check. Renders the modal's body and
// footer.
//
// Props: mode 'save' | 'update'; template (the one updated, or null);
// groups (this show's GroupViews); defaultName (a new template's name);
// busy (the write is in flight); error (its axios error, or null);
// onSubmit({ name, groupIds }); onBack().

const CARRIED_HINT = "Each group's sections are matched by name on every show it is applied to. "
  + 'It carries rows, Qty and splits, the notes filter, the ladder, hold and Live qty, the no-comp step-down, '
  + "outliers, the zone fallback and the group's own floor. Groups it makes start with Auto off.";
const UPDATE_HINT = 'The shows it was applied to read "Older version" until you apply it to them again.';
const BUSY_TITLE = 'Wait for the save to finish';

function GroupRow({ row, checked, disabled, onToggle }) {
  const { group: g, reason, linked } = row;
  let tickTitle;
  if (reason) tickTitle = `Can't go in a template: ${reason}`;
  else if (disabled) tickTitle = BUSY_TITLE;
  const sections = Array.isArray(g.criteria?.sections) ? g.criteria.sections : [];
  const rows = Array.isArray(g.criteria?.rowsInclude) ? g.criteria.rowsInclude : [];
  return (
    <tr className={checked ? undefined : 'off'}>
      <td>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled || !!reason}
          title={tickTitle}
          onChange={() => onToggle(String(g.id))}
          aria-label={`Put ${g.name} in the template`}
        />
      </td>
      <td className="la-import-name" title={g.name}>
        {g.name}
        {!!g.autoEnabled && <span className="la-auto-chip" title="This group is on Auto">Auto</span>}
      </td>
      <td title={sections.length ? sectionsText(sections, Infinity) : undefined}>{sectionsText(sections, 4)}</td>
      <td title={rows.length ? rows.join(', ') : undefined}>{rowsText(rows, 4)}</td>
      <td>{qtyText(g.criteria?.minQuantity)}</td>
      <td>
        {reason && <span className="la-hint warn">{reason}</span>}
        {!reason && linked && <span className="la-hint">in this template</span>}
      </td>
    </tr>
  );
}

function LaTemplateSaveForm({
  mode = 'save', template = null, groups = [], defaultName = '', busy = false, error = null, onSubmit, onBack,
}) {
  const nameId = useId();
  const isUpdate = mode === 'update';
  // null = untouched: the defaults below
  const [nameState, setNameState] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const rows = saveableGroups(groups, isUpdate ? (template?.id ?? null) : null);
  const selected = selectedState ?? defaultSaveSelection(rows);
  const name = nameState ?? (isUpdate ? (template?.name || '') : defaultName);
  const nameError = templateNameError(name);
  const groupIds = saveGroupIds(selected, rows);
  const tickable = rows.filter(r => !r.reason).map(r => String(r.group.id));
  const allTicked = everyTicked(selected, tickable);
  const unlinking = isUpdate ? rows.filter(r => r.linked && !selected.has(String(r.group.id))).length : 0;
  const canSubmit = !busy && !nameError && groupIds.length > 0;

  const handleToggle = (key) => setSelectedState(prev => toggleSelection(prev ?? selected, key));
  const handleToggleAll = () => setSelectedState(allTicked ? new Set() : new Set(tickable));
  const handleSubmit = () => {
    if (canSubmit) onSubmit({ name: name.trim(), groupIds });
  };
  const handleNameKey = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  const errorInfo = error ? templateErrorInfo(error) : null;
  let submitTitle;
  if (busy) submitTitle = BUSY_TITLE;
  else if (nameError) submitTitle = nameError;
  else if (!groupIds.length) submitTitle = 'Tick at least one group';
  let submitLabel = isUpdate ? `Update with ${groupIds.length}` : `Save ${groupIds.length}`;
  if (busy) submitLabel = isUpdate ? 'Updating…' : 'Saving…';

  const body = !rows.length ? (
    <div className="la-empty">This show has no groups yet. Build them here first, then save them as a template.</div>
  ) : (
    <table className="la-import-table">
      <thead>
        <tr>
          <th>
            <input
              type="checkbox"
              checked={allTicked}
              disabled={busy || !tickable.length}
              title={busy ? BUSY_TITLE : (tickable.length ? undefined : 'No group here can go in a template')}
              onChange={handleToggleAll}
              aria-label="Tick every group that can go in the template"
            />
          </th>
          <th>Group</th>
          <th>Sections</th>
          <th>Rows</th>
          <th>Qty</th>
          <th>Note</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <GroupRow
            key={row.group.id}
            row={row}
            checked={!row.reason && selected.has(String(row.group.id))}
            disabled={busy}
            onToggle={handleToggle}
          />
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      <div className="modal-body la-template-body">
        <div className="la-template-name-row">
          <label htmlFor={nameId} className="la-muted">Name</label>
          <input
            id={nameId}
            className={`form-input form-input-sm la-template-name-input${nameError ? ' la-input-bad' : ''}`}
            value={name}
            maxLength={TEMPLATE_NAME_MAX}
            disabled={busy}
            onChange={(e) => setNameState(e.target.value)}
            onKeyDown={handleNameKey}
          />
          {nameError && <span className="la-hint warn">{nameError}</span>}
        </div>
        {body}
        <div className="la-import-warnings">
          {unlinking > 0 && (
            <span className="la-hint warn">
              {unlinking === 1
                ? '1 group linked to this template is unticked: it is unlinked and stays here as a hand-made group.'
                : `${unlinking} groups linked to this template are unticked: they are unlinked and stay here as hand-made groups.`}
            </span>
          )}
          {isUpdate && <span className="la-hint">{UPDATE_HINT}</span>}
          <span className="la-hint">{CARRIED_HINT}</span>
        </div>
      </div>
      <div className="modal-footer bulk-review-footer">
        <span className="ai-admin-note" aria-live="polite">{`${groupIds.length} of ${tickable.length} ticked`}</span>
        {errorInfo && (
          <span className={errorInfo.kind === 'warn' ? 'la-template-unknown' : 'ai-inline-error'} aria-live="polite">
            {errorInfo.text}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn-secondary" onClick={onBack} disabled={busy} title={busy ? BUSY_TITLE : undefined}>
          Back
        </button>
        <button type="button" className="btn-primary" onClick={handleSubmit} disabled={!canSubmit} title={submitTitle}>
          {submitLabel}
        </button>
      </div>
    </>
  );
}

export default LaTemplateSaveForm;
